import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchCalendar } from "../apis/calendar";
import useAutoRefresh from "../hooks/useAutoRefresh";
import {
  TYPE_CIRCULAR,
  TYPE_TASK,
  TYPE_TODO,
  dayKey,
  fmtEventTime,
  normalizeEvents,
} from "../utils/calendarEvents";
import "../styles/components/TodaySchedule.css";

const GROUPS = [
  { type: TYPE_TASK, label: "Tasks", tone: "blue" },
  { type: TYPE_TODO, label: "Todo", tone: "green" },
  { type: TYPE_CIRCULAR, label: "Circular", tone: "orange" },
];

const GroupIcon = ({ tone }) => (
  <svg
    viewBox="0 0 24 24"
    aria-hidden="true"
    className={`schedule-group-icon ${tone}`}
  >
    <circle cx="12" cy="12" r="9" fill="currentColor" opacity="0.16" />
    <path
      d="M8 12.3l2.6 2.6L16 9.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const badgeFor = (event) => {
  if (event.type === TYPE_CIRCULAR) {
    return event.isRead === false
      ? { label: "Unread", tone: "orange" }
      : { label: "Read", tone: "grey" };
  }
  if (event.completed) return { label: "Completed", tone: "green" };
  if (event.overdue) return { label: "Overdue", tone: "red" };
  if (event.type === TYPE_TODO && event.priority === "high") {
    return { label: "High Priority", tone: "red" };
  }
  return { label: "Pending", tone: "blue" };
};

const TodaySchedule = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const today = useMemo(() => dayKey(new Date()), []);

  const loadSchedule = useCallback(async () => {
    try {
      const res = await fetchCalendar({ from: today, to: today });
      if (res?.error) return;
      setEvents(normalizeEvents(res).filter((e) => e.day === today));
    } catch (error) {
    } finally {
      setLoaded(true);
    }
  }, [today]);

  useEffect(() => {
    loadSchedule();
  }, [loadSchedule]);

  useAutoRefresh(() => loadSchedule());

  const groups = useMemo(
    () =>
      GROUPS.map((g) => ({
        ...g,
        items: events
          .filter((e) => e.type === g.type)
          .sort((a, b) => (a.time || "99:99").localeCompare(b.time || "99:99")),
      })).filter((g) => g.items.length > 0),
    [events],
  );

  const stats = useMemo(
    () => ({
      pending: events.filter(
        (e) => e.type !== TYPE_CIRCULAR && !e.completed && !e.overdue,
      ).length,
      overdue: events.filter((e) => e.type !== TYPE_CIRCULAR && e.overdue)
        .length,
      unread: events.filter(
        (e) => e.type === TYPE_CIRCULAR && e.isRead === false,
      ).length,
      total: events.length,
    }),
    [events],
  );

  if (!loaded) return null;

  const headingDate = new Date(`${today}T00:00:00`).toLocaleDateString([], {
    month: "short",
    day: "numeric",
  });

  return (
    <section className="today-schedule" aria-label="Today's Schedule">
      <div className="today-schedule-head">
        <h3 className="today-schedule-title">
          Today&apos;s Schedule · {headingDate}
        </h3>
        <button
          type="button"
          className="today-schedule-link"
          onClick={() => navigate("/tasks")}
        >
          View Calendar <span aria-hidden="true">&rarr;</span>
        </button>
      </div>

      {groups.length === 0 ? (
        <p className="today-schedule-empty">Nothing scheduled for today.</p>
      ) : (
        <div className="today-schedule-groups">
          {groups.map((group) => (
            <article
              className={`schedule-group ${group.tone}`}
              key={group.type}
            >
              <div className="schedule-group-head">
                <GroupIcon tone={group.tone} />
                <span>{group.label}</span>
              </div>
              <ul className="schedule-list">
                {group.items.map((event) => {
                  const badge = badgeFor(event);
                  return (
                    <li className="schedule-item" key={event.id}>
                      <span className="schedule-item-title" title={event.title}>
                        {event.title}
                      </span>
                      <span className="schedule-item-time">
                        {fmtEventTime(event.time)}
                      </span>
                      <span className={`schedule-item-badge ${badge.tone}`}>
                        {badge.label}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </article>
          ))}
        </div>
      )}

      <div className="today-schedule-stats">
        <div className="schedule-stat blue">
          <p className="schedule-stat-value">{stats.pending}</p>
          <p className="schedule-stat-label">Pending</p>
        </div>
        <div className="schedule-stat red">
          <p className="schedule-stat-value">{stats.overdue}</p>
          <p className="schedule-stat-label">Overdue</p>
        </div>
        <div className="schedule-stat orange">
          <p className="schedule-stat-value">{stats.unread}</p>
          <p className="schedule-stat-label">Unread</p>
        </div>
        <div className="schedule-stat grey">
          <p className="schedule-stat-value">{stats.total}</p>
          <p className="schedule-stat-label">Total</p>
        </div>
      </div>
    </section>
  );
};

export default TodaySchedule;
